/**
 * Open / save a file cross-platform.
 *
 * On a Capacitor device the WKWebView won't open `blob:` URLs in a new tab and
 * ignores the `<a download>` attribute, so view/download silently no-op there.
 * Instead we write the bytes to the app's cache directory and hand the file to
 * the native share sheet — which lets the user preview, "Save to Files", or open
 * it in another app. On the web we fall back to a normal download anchor.
 */
import { Capacitor } from "@capacitor/core";

async function urlToBlob(url: string): Promise<Blob> {
  return fetch(url).then((r) => r.blob());
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Save raw bytes as `filename`. On a Capacitor device the bytes are written to
 * the cache directory and handed to the native share sheet (the only reliable
 * way to "download" from a WebView); on the web a download anchor is used.
 *
 * Use this whenever you already hold the data as a Blob (e.g. a generated PDF /
 * spreadsheet pulled from an authed endpoint) — see `downloadAuthedFile`.
 */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const safeName = filename.replace(/[^\w.\-]+/g, "_") || "download";
  if (Capacitor.isNativePlatform()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import("@capacitor/filesystem"),
      import("@capacitor/share"),
    ]);
    const data = await blobToBase64(blob);
    const { uri } = await Filesystem.writeFile({ path: safeName, data, directory: Directory.Cache });
    await Share.share({ title: filename, url: uri });
    return;
  }
  // Web: a plain download anchor. The bytes are already in hand (same-origin
  // blob), so the `download` attribute is honoured.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeName;
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Native-only: download a remote file straight to disk and hand it to the share
 * sheet. The bytes are fetched by native code and written directly to the cache
 * directory, so — unlike a WebView `fetch().blob()` with CapacitorHttp enabled —
 * the binary never round-trips through the JS bridge (which can corrupt non-text
 * bodies, leaving an empty/garbled PDF). `headers` carries the bearer token.
 *
 * Throws if the download fails; callers surface that to the user.
 */
export async function downloadRemoteFileNative(
  url: string,
  filename: string,
  headers?: Record<string, string>,
): Promise<void> {
  const safeName = filename.replace(/[^\w.\-]+/g, "_") || "download";
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  await Filesystem.downloadFile({ url, path: safeName, directory: Directory.Cache, method: "GET", headers });
  const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path: safeName });
  await Share.share({ title: filename, url: uri });
}

/** View/download `url` (a cached `blob:` URL works offline) as `filename`. */
export async function shareOrDownload(url: string, filename: string): Promise<void> {
  // Native: pull the bytes (a cached blob: URL is fetchable offline) and route
  // through the share sheet via saveBlob.
  if (Capacitor.isNativePlatform()) {
    return saveBlob(await urlToBlob(url), filename);
  }
  // Web: trigger a download anchor. The `download` attribute is honoured only for
  // same-origin (and blob:/data:) URLs — for a cross-origin signed URL the
  // browser ignores it and just opens the file in the tab. So fetch a remote URL
  // into a same-origin blob first; blob:/data: URLs download directly.
  const safeName = filename.replace(/[^\w.\-]+/g, "_") || "attachment";
  let href = url;
  let createdObjectUrl = false;
  if (!/^(blob:|data:)/i.test(url)) {
    try {
      href = URL.createObjectURL(await urlToBlob(url));
      createdObjectUrl = true;
    } catch {
      // CORS / network failure — open it so the user can still save manually.
      window.open(url, "_blank", "noopener");
      return;
    }
  }
  // Anchor at `href` (the same-origin blob when we re-fetched, else the URL
  // itself) and let the `download` attribute name the saved file.
  const a = document.createElement("a");
  a.href = href;
  a.download = safeName;
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  if (createdObjectUrl) setTimeout(() => URL.revokeObjectURL(href), 1500);
}
